import CacheStorage from "utils/cache/CacheStorage";
import FetchCachedSettings from "utils/fetch/cached/FetchCachedSettings";
import FetchCachedLanguages from "utils/fetch/cached/FetchCachedLanguages";
import FetchCachedBuilding from "utils/fetch/cached/FetchCachedBuilding";
import FetchCachedFloor from "utils/fetch/cached/FetchCachedFloor";
import FetchCachedRoom from "utils/fetch/cached/FetchCachedRoom";
import FetchCachedLunchOption from "utils/fetch/cached/FetchCachedLunchOption";
import FetchCachedMyBooking from "utils/fetch/cached/FetchCachedMyBooking";
import FetchCachedDepartment from "utils/fetch/cached/FetchCachedDepartment";
import _ from "lodash";
import FetchCachedEmployee from "utils/fetch/cached/FetchCachedEmployee";
import FetchCachedFavourites from "utils/fetch/cached/FetchCachedFavourites";
import FetchCachedTeam from "utils/fetch/cached/FetchCachedTeam";
import FetchCachedEmployeeDetails from 'utils/fetch/cached/FetchCachedEmployeeDetails'
import FetchCachedMessage from 'utils/fetch/cached/FetchCachedMessage'
import FetchCachedEmployeeEros from 'utils/fetch/cached/FetchCachedEmployeeEros'
import FetchCachedEmployeeConfidants from 'utils/fetch/cached/FetchCachedEmployeeConfidants'
import CacheByUpdatedAt from 'utils/cache/CacheByUpdatedAt'
import FetchCachedMyEmployee from 'utils/fetch/cached/FetchCachedMyEmployee'
import FetchCachedMyPastBooking from 'utils/fetch/cached/FetchCachedMyPastBooking'
import FetchCachedOtherEmployeeBookings from 'utils/fetch/cached/FetchCachedOtherEmployeeBookings'
import FetchCachedTimeslotKinds from 'utils/fetch/cached/FetchCachedTimeslotKinds'
import FetchCachedTimeslotGroups from 'utils/fetch/cached/FetchCachedTimeslotGroups'
import FetchCachedParkingStatuses from 'utils/fetch/cached/FetchCachedParkingStatuses'
import FetchCachedAppMessages from 'utils/fetch/cached/FetchCachedAppMessages'

export const HelperCacheFetchConstants = {
	BEFORE: 'before',
	AFTER: 'after',
}

const CompleteStatus = {
	WORKING: 'working',
	COMPLETE: 'complete',
	ERROR: 'error'
}

const HelperCache = {

	constructed: false,
	fetchResults: {},
	fetchers: {},
	
	constructing: null,
	construct: async ({ onConstructed } = {}) => {
		if( !HelperCache.constructed ) {
			if( !HelperCache.constructing ) {
				HelperCache.constructing = HelperCache.initialize()
					.then( () => {
						HelperCache.constructed = true;
					} )
					.finally( () => {
						HelperCache.constructing = null;
					} );
			}
			await HelperCache.constructing;
		}
		onConstructed?.();
	},

	initialize: async () => {
		// Create cache storage
		CacheStorage.construct();
		await CacheStorage.getFromDevice();
		
		// Validate the cache
		if( CacheStorage.isCacheEmpty() ) {
			CacheByUpdatedAt.setForceReload( true );
		}
		
		// Hydrate independent employee caches in parallel.
		await Promise.all([
			FetchCachedEmployee.construct_all(),
			FetchCachedEmployeeDetails.construct_all(),
		]);
		
		// set the fetchers
		HelperCache.fetchers = {
			// Always required at boot
			[ HelperCacheFetchConstants.BEFORE ]: [
				FetchCachedSettings,
				FetchCachedMyEmployee,
				FetchCachedLanguages,
				
				// Employee
				FetchCachedMyBooking,
				FetchCachedFavourites,
				
				
				// Building data
				FetchCachedTimeslotKinds,
				FetchCachedTimeslotGroups,
				FetchCachedBuilding,
				FetchCachedFloor,
				FetchCachedRoom,
			],
			[ HelperCacheFetchConstants.AFTER ]: [
				// General employee data
				FetchCachedAppMessages,
				FetchCachedTeam,
				FetchCachedDepartment,
				FetchCachedLunchOption,
				FetchCachedMyPastBooking,
				
				// Ero's
				FetchCachedOtherEmployeeBookings,
				FetchCachedEmployeeEros,
				FetchCachedEmployeeConfidants,
				
				// Specific employee data
				FetchCachedMessage,
				
				// Parking
				FetchCachedParkingStatuses,
			]
		}
		
		await Promise.all(
			Object.values( HelperCache.fetchers )
				.flat()
				.map( fetcher => {
					return fetcher.construct();
				} )
		);

		await new Promise( resolve => {
			return CacheByUpdatedAt.initialized(resolve);
		} );
	},
	
	fetchAll: ({onPercentageUpdate, onCompleteAll, onError, fetchKey } = {}) => {
		// Set and reset
		const fetchers = HelperCache.fetchers[ fetchKey ];
		HelperCache.fetchers[ fetchKey ] = [];
		
		// Init the unique key
		HelperCache.fetchResults[ fetchKey ] = {
			__functions: {
				onPercentageUpdate,
				onCompleteAll,
				onError
			},
			fetchers: {},
		};
		
		// Go through the fetchers and fetch them all
		_.forEach( fetchers, ( fetcher ) => {
			HelperCache.onFetchStart( fetchKey, fetcher )
		});
		
		//
		_.forEach( fetchers, ( fetcher ) => {
			HelperCache.fetch( fetchKey, fetcher );
		});
	},

	fetch: (fetchKey, object ) => {
		switch( true ) {
			case _.isFunction( object.fetchAll ):
				object.fetchAll({
					callbackSuccess: () => {
						HelperCache.onFetchSuccess( fetchKey, object )
					},
					callbackError: () => {
						HelperCache.onFetchError( fetchKey, object )
					}
				});
				break;
				
			case _.isFunction( object.construct ):
				object.construct.then( () => {
					return HelperCache.onFetchSuccess(fetchKey, object);
				} )
				break;
				
			default:
				HelperCache.onFetchSuccess( fetchKey, object )
		}
	},
	
	onFetchStart: ( fetchKey, object ) => {
		if( !_.has( HelperCache.fetchResults[ fetchKey ].fetchers, object.name ) ) {
			HelperCache.fetchResults[ fetchKey ].fetchers[ object.name ] = null
		}
	},
	
	onFetchSuccess: ( fetchKey, object ) => {
		HelperCache.fetchResults[ fetchKey ].fetchers[ object.name ] = true
		
		HelperCache.validateFetches( fetchKey )
	},
	
	onFetchError: ( fetchKey, object ) => {
		HelperCache.fetchResults[ fetchKey ].fetchers[ object.name ] = false
		
		HelperCache.validateFetches( fetchKey )
	},
	
	validateFetches: ( fetchKey ) => {
		const fetchResultObject = HelperCache.fetchResults[ fetchKey ];
		
		const { onPercentageUpdate, onCompleteAll, onError } = fetchResultObject.__functions;
		const fetchers = fetchResultObject.fetchers;
		
		if( _.isFunction( onPercentageUpdate ) ) {
			onPercentageUpdate( Math.round( ( _.countBy( fetchers, o => {
				return o === true;
			})?.true ?? 0 / _.size( fetchers ) ) * 9 ) ); // We do 90% since we need to fake the last 10% for showing the splash screen -_-
		}
		
		let completeStatus = CompleteStatus.COMPLETE;
		_.forEach( fetchers, ( value, key ) => {
			if( value === false ) {
				completeStatus = CompleteStatus.ERROR;
				return false;
			}
			
			if( value === null ) {
				completeStatus = CompleteStatus.WORKING;
				return false;
			}
		})
		
		// Not returned? then it is ok
		if( completeStatus === CompleteStatus.COMPLETE ) {
			if( _.isFunction( onCompleteAll ) ) {
				onCompleteAll();
			}
			return;
		}
		
		// Trigger error
		if( completeStatus === CompleteStatus.ERROR ) {
			if( _.isFunction( onError ) ) {
				onError();
			}
			return;
		}
		
		// Nothingg
	},
	
	canFetchAgain: ( object ) => {
		const name = object.name
		
		if( _.has( HelperCache.fetchResults, name ) ) {
			return HelperCache.fetchResults[ name ] <= 3;
		}
		
		return true;
	}
}

export default HelperCache
